import React, { useEffect, useRef, useState } from "react";
import moment from "moment";
import {
    FeedActionButton,
    FeedActionRow,
    FeedBadge,
    FeedCardBody,
    FeedCardHeader,
    FeedCardSurface,
    FeedCreator,
    FeedDescription,
    FeedIdentity,
    FeedInlineState,
    FeedMeta,
    FeedPinIcon,
    FeedPinIconButton,
    FeedPostButton,
    FeedReactionChoice,
    FeedReactionPopup,
    FeedReactionPicker,
    FeedReactionQuickList,
    FeedMoreReactionButton,
    FeedSummaryButton,
    FeedSummaryRow,
    FeedTextButton,
    FeedTitle
} from "src/components/NoticeboardFeedUi";
import NoticePinIcon from "src/img/notice_pin.png";
import NoticePinOutlineIcon from "src/img/notice_pin_outline.png";
import {
    pinNoticePublication,
    removeNoticeReaction,
    setNoticeReaction,
    unpinNoticePublication
} from "../../api/feedApi";
import {
    getNoticeAudienceLabel,
    getNoticeAuthorIdentity,
    getNoticeCommentCount,
    getNoticeInteractionUuid,
    getNoticePublication,
    incrementNoticeCommentCount,
    isNoticePinnedForAudience,
    setNoticePublicationPinned
} from "../../feedUtils";
import { NOTICEBOARD_ALL_REACTIONS, NOTICEBOARD_QUICK_REACTIONS } from "../../constants";
import { hasValidCheckIn } from "../../checkIn";
import { CheckInDisplay } from "../CheckInSection";
import NoticeboardFeedAttachments from "./NoticeboardFeedAttachments";
import NoticeboardFeedAvatar from "./NoticeboardFeedAvatar";
import NoticeboardFeedCommentPreview from "./NoticeboardFeedComments";
import NoticeboardFeedMedia from "./NoticeboardFeedMedia";
import NoticeboardPostDetailModal from "./NoticeboardPostDetailModal";
import NoticeboardReactionsModal from "./NoticeboardReactionsModal";

const DESCRIPTION_PREVIEW_LENGTH = 420;

const NoticeboardFeedCard = ({
    notice,
    token,
    audience,
    employeeName,
    clientName,
    canManageActiveNoticeboard,
    canUseCheckedIn,
    onNoticeUpdated
}) => {
    const [expanded, setExpanded] = useState(false);
    const [showReactionPicker, setShowReactionPicker] = useState(false);
    const [reactionLoading, setReactionLoading] = useState(false);
    const [reactionError, setReactionError] = useState(false);
    const [showReactions, setShowReactions] = useState(false);
    const [showAllReactions, setShowAllReactions] = useState(false);
    const [showInlineComposer, setShowInlineComposer] = useState(false);
    const [showDetail, setShowDetail] = useState(false);
    const [pinLoading, setPinLoading] = useState(false);
    const [pinError, setPinError] = useState(false);
    const reactionPickerRef = useRef(null);
    const moreReactionButtonRef = useRef(null);
    const reactionActionButtonRef = useRef(null);

    useEffect(() => {
        setExpanded(false);
        setShowReactionPicker(false);
        setShowAllReactions(false);
        setReactionError(false);
        setPinError(false);
    }, [notice.uuid]);

    useEffect(() => {
        setShowAllReactions(false);
    }, [notice]);

    useEffect(() => {
        if (!showAllReactions) {
            return undefined;
        }

        const handlePointerDown = (event) => {
            if (!reactionPickerRef.current?.contains(event.target)) {
                setShowAllReactions(false);
            }
        };
        const handleKeyDown = (event) => {
            if (event.key === "Escape") {
                setShowAllReactions(false);
                moreReactionButtonRef.current?.focus();
            }
        };
        document.addEventListener("mousedown", handlePointerDown);
        document.addEventListener("keydown", handleKeyDown);

        return () => {
            document.removeEventListener("mousedown", handlePointerDown);
            document.removeEventListener("keydown", handleKeyDown);
        };
    }, [showAllReactions]);

    const interactionUuid = getNoticeInteractionUuid(notice, audience);
    const activePublication = getNoticePublication(notice, audience);
    const canChangePin = canManageActiveNoticeboard && Boolean(activePublication);
    const isPinned = isNoticePinnedForAudience(notice, audience);
    const commentCount = getNoticeCommentCount(notice, audience);
    const creatorName = notice.postedBy || "Unavailable user";
    const creatorIdentity = getNoticeAuthorIdentity(notice);
    const publishedDate = notice.postedDate || notice.created_at;
    const audienceLabel = audience ?
        getNoticeAudienceLabel(notice, employeeName, clientName) :
        "";
    const message = notice.message || "";
    const isCheckedIn = canUseCheckedIn && hasValidCheckIn(notice.checkIn);
    const hasLongMessage = message.length > DESCRIPTION_PREVIEW_LENGTH;
    const visibleMessage = hasLongMessage && !expanded ?
        `${message.slice(0, DESCRIPTION_PREVIEW_LENGTH).trimEnd()}…` :
        message;

    const handleReaction = async (unicode) => {
        if (reactionLoading) {
            return;
        }

        setReactionLoading(true);
        setReactionError(false);
        try {
            const updatedNotice = notice.reactions.currentUserReaction === unicode ?
                await removeNoticeReaction(token, interactionUuid) :
                await setNoticeReaction(token, interactionUuid, unicode);
            onNoticeUpdated(updatedNotice);
            setShowReactionPicker(false);
            setShowAllReactions(false);
            reactionActionButtonRef.current?.focus();
        } catch {
            setReactionError(true);
        } finally {
            setReactionLoading(false);
        }
    };

    const handleCommentCreated = () => {
        onNoticeUpdated(incrementNoticeCommentCount(notice, interactionUuid));
    };

    const handlePinChange = async () => {
        if (!activePublication || pinLoading) {
            return;
        }

        setPinLoading(true);
        setPinError(false);
        try {
            if (activePublication.isPinned) {
                await unpinNoticePublication(token, activePublication.uuid);
            } else {
                await pinNoticePublication(token, activePublication.uuid);
            }
            onNoticeUpdated(
                setNoticePublicationPinned(
                    notice,
                    activePublication.uuid,
                    !activePublication.isPinned
                )
            );
        } catch {
            setPinError(true);
        } finally {
            setPinLoading(false);
        }
    };

    return (
        <>
            <FeedCardSurface>
                {canChangePin && (
                    <FeedPinIconButton
                        type="button"
                        $active={isPinned}
                        disabled={pinLoading}
                        aria-busy={pinLoading}
                        aria-label={`${isPinned ? "Unpin" : "Pin"} ${notice.subject || "Noticeboard post"}`}
                        aria-pressed={isPinned}
                        title={pinLoading ? "Saving pinned status…" : isPinned ? "Unpin post" : "Pin post"}
                        onClick={handlePinChange}
                    >
                        <FeedPinIcon
                            src={isPinned ? NoticePinIcon : NoticePinOutlineIcon}
                            $active={isPinned}
                            alt=""
                            aria-hidden="true"
                        />
                    </FeedPinIconButton>
                )}
                <FeedPostButton
                    type="button"
                    aria-label={`Open ${notice.subject || "Noticeboard post"} details`}
                    onClick={() => setShowDetail(true)}
                >
                    <FeedCardHeader
                        $hasAvatar={Boolean(creatorIdentity.displayName)}
                        $hasPinButton={canChangePin}
                    >
                        <NoticeboardFeedAvatar
                            displayName={creatorIdentity.displayName}
                            profilePictureUrl={creatorIdentity.profilePictureUrl}
                            size="2.75rem"
                            fontSize="0.8rem"
                        />
                        <FeedIdentity>
                            <FeedCreator>
                                {isCheckedIn ? `${creatorName} Checked In to` : `Created by ${creatorName}`}
                            </FeedCreator>
                            <FeedMeta>
                                <time dateTime={publishedDate}>
                                    {moment.utc(publishedDate).local().format("D MMM YYYY [at] h:mm A")}
                                </time>
                                {audienceLabel && <FeedBadge>{audienceLabel}</FeedBadge>}
                                {isPinned && <FeedBadge $important>Pinned</FeedBadge>}
                            </FeedMeta>
                        </FeedIdentity>
                    </FeedCardHeader>
                    <FeedCardBody>
                        {notice.subject && <FeedTitle>{notice.subject}</FeedTitle>}
                        {visibleMessage && <FeedDescription>{visibleMessage}</FeedDescription>}
                    </FeedCardBody>
                </FeedPostButton>
                {hasLongMessage && (
                    <FeedCardBody>
                        <FeedTextButton type="button" onClick={() => setExpanded(current => !current)}>
                            {expanded ? "Show less" : "Show more"}
                        </FeedTextButton>
                    </FeedCardBody>
                )}
                <NoticeboardFeedMedia
                    notice={notice}
                    onOpenPost={() => setShowDetail(true)}
                    modalMode={false}
                    initialIndex={0}
                />
                <NoticeboardFeedAttachments attachments={notice.attachments} />
                <CheckInDisplay checkIn={canUseCheckedIn ? notice.checkIn : null} />
                <FeedSummaryRow>
                    <FeedSummaryButton
                        type="button"
                        onClick={() => setShowReactions(true)}
                        aria-label={`View ${notice.reactions.total} post reactions`}
                    >
                        <span aria-hidden="true">
                            {notice.reactions.emojis.slice(0, 3).map(reaction => reaction.unicode).join(" ")}
                        </span>
                        {notice.reactions.total} {notice.reactions.total === 1 ? "reaction" : "reactions"}
                    </FeedSummaryButton>
                    {notice.allowComments && (
                        <FeedSummaryButton
                            type="button"
                            aria-expanded={showInlineComposer}
                            onClick={() => setShowInlineComposer(current => !current)}
                        >
                            {commentCount} {commentCount === 1 ? "comment" : "comments"}
                        </FeedSummaryButton>
                    )}
                </FeedSummaryRow>
                <FeedActionRow>
                    <FeedActionButton
                        ref={reactionActionButtonRef}
                        type="button"
                        $active={Boolean(notice.reactions.currentUserReaction)}
                        aria-expanded={showReactionPicker}
                        onClick={() => {
                            setShowReactionPicker(current => !current);
                            setShowAllReactions(false);
                        }}
                    >
                        {notice.reactions.currentUserReaction || "👍"} React
                    </FeedActionButton>
                    <FeedActionButton
                        type="button"
                        aria-expanded={notice.allowComments ? showInlineComposer : undefined}
                        onClick={() => setShowInlineComposer(current => !current)}
                        disabled={!notice.allowComments}
                    >
                        💬 Comment
                    </FeedActionButton>
                </FeedActionRow>
                {showReactionPicker && (
                    <FeedReactionPicker ref={reactionPickerRef} aria-label="Choose a reaction">
                        <FeedReactionQuickList>
                            {NOTICEBOARD_QUICK_REACTIONS.map(unicode => (
                                <FeedReactionChoice
                                    key={unicode}
                                    type="button"
                                    $active={notice.reactions.currentUserReaction === unicode}
                                    disabled={reactionLoading}
                                    aria-label={`React with ${unicode}`}
                                    onClick={() => handleReaction(unicode)}
                                >
                                    {unicode}
                                </FeedReactionChoice>
                            ))}
                        </FeedReactionQuickList>
                        <FeedMoreReactionButton
                            ref={moreReactionButtonRef}
                            type="button"
                            aria-expanded={showAllReactions}
                            aria-haspopup="dialog"
                            onClick={() => setShowAllReactions(current => !current)}
                        >
                            ☺ More
                        </FeedMoreReactionButton>
                        {showAllReactions && (
                            <FeedReactionPopup role="dialog" aria-label="More reactions">
                                {NOTICEBOARD_ALL_REACTIONS.map(unicode => (
                                    <FeedReactionChoice
                                        key={unicode}
                                        type="button"
                                        $active={notice.reactions.currentUserReaction === unicode}
                                        disabled={reactionLoading}
                                        aria-label={`React with ${unicode}`}
                                        onClick={() => handleReaction(unicode)}
                                    >
                                        {unicode}
                                    </FeedReactionChoice>
                                ))}
                            </FeedReactionPopup>
                        )}
                    </FeedReactionPicker>
                )}
                {reactionError && (
                    <FeedInlineState $compact $error role="alert">
                        Your reaction could not be saved. Please try again.
                    </FeedInlineState>
                )}
                {pinError && (
                    <FeedInlineState $compact $error role="alert">
                        This post’s pinned status could not be changed. Please try again.
                    </FeedInlineState>
                )}
                {showInlineComposer && notice.allowComments && (
                    <NoticeboardFeedCommentPreview
                        token={token}
                        noticeUuid={notice.uuid}
                        interactionUuid={interactionUuid}
                        commentCount={commentCount}
                        allowComments={notice.allowComments}
                        showComposer
                        onCommentCreated={handleCommentCreated}
                    />
                )}
            </FeedCardSurface>

            <NoticeboardReactionsModal
                open={showReactions}
                onClose={() => setShowReactions(false)}
                token={token}
                interactionUuid={interactionUuid}
            />
            <NoticeboardPostDetailModal
                open={showDetail}
                onClose={() => setShowDetail(false)}
                token={token}
                notice={notice}
                interactionUuid={interactionUuid}
                employeeName={employeeName}
                clientName={clientName}
                audience={audience}
                showAudience={Boolean(audience)}
                canUseCheckedIn={canUseCheckedIn}
                onOpenReactions={() => setShowReactions(true)}
                onCommentCreated={handleCommentCreated}
            />
        </>
    );
};

export default NoticeboardFeedCard;
